import { CSSProperties, useCallback, useEffect, useId, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation, Trans } from 'react-i18next';
import { useOnboardingState } from 'state/useOnboardingState';
import { useDrawerNavState } from 'state/useDrawerNavState';
import { useLayersDrawerState } from 'features/mapping/hooks/useLayersDrawerState';
import { SEARCH_DRAWER_WIDTH } from '../../constants';
import { VectorSettings } from './VectorSettings';
import { VectorLayers } from './VectorLayers';
import { VectorLegends } from './VectorLegends';

type CutoutRect = { x: number; y: number; w: number; h: number };
type Cutouts = {
  settings?: CutoutRect;
  layers?: CutoutRect;
  legend?: CutoutRect;
};

const HAMBURGER_CUTOUT_SIZE = 42;
const CUTOUT_RADIUS = 8;

const MEASURE_RETRY_INTERVAL_MS = 100;
const MEASURE_MAX_RETRIES = 50;

const MAP_CONTROL_SIZE = 56;
const MAP_CONTROL_RIGHT = 16;
const MAP_CONTROL_GAP = 16;

const HEADER_HEIGHT_MOBILE = 56;
const HEADER_HEIGHT_DESKTOP = 64;
/** MUI Toolbar switches from 56→64px at the `sm` (600px) breakpoint. */
const HEADER_BREAKPOINT_PX = 600;

/** At ≥768px the SearchDrawer is visible, shifting map controls left. */
const SEARCH_DRAWER_BREAKPOINT_PX = 768;

// Label containers are positioned via hardcoded math so the visual label/arrow
// lands near the icon. The interactive tap-target buttons are positioned
// separately from the measured cutouts so they always overlap the real icon.
const LAYERS_LABEL_OFFSET = { top: 36, right: 64 };
const LEGEND_LABEL_OFFSET = { top: 62, right: 10 };

const useStyles = makeStyles()((theme) => ({
  root: {
    position: 'fixed',
    inset: 0,
    zIndex: theme.zIndex.drawer,
    pointerEvents: 'none',
  },
  dim: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    // Re-enable pointer events so taps on the dim area are absorbed.
    pointerEvents: 'auto',
  },
  container: {
    position: 'absolute',
    inset: 0,
    padding: theme.spacing(2),
    paddingBottom: 'max(env(safe-area-inset-bottom), 16px)',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  button: {
    pointerEvents: 'auto',
    [theme.breakpoints.up(480)]: {
      maxWidth: 350,
    },
    border:
      theme.palette.mode === 'light'
        ? `4px solid ${theme.palette.primary.contrastText}`
        : undefined,
  },
  label: {
    backgroundColor: theme.palette.brand[500],
    color: theme.palette.warmGrey[950],
    borderRadius: Number(theme.shape.borderRadius) * 1.35,
    padding: theme.spacing(1, 2),
    lineHeight: '22px',
  },
  settingsContainer: {
    position: 'absolute',
    display: 'flex',
    alignItems: 'flex-end',
    top: `calc(${HAMBURGER_CUTOUT_SIZE + 12}px + env(safe-area-inset-top))`,
    left: `calc(${Math.round(HAMBURGER_CUTOUT_SIZE / 2) + 7}px + env(safe-area-inset-left))`,
    [theme.breakpoints.up(HEADER_BREAKPOINT_PX)]: {
      top: `calc(${HAMBURGER_CUTOUT_SIZE + 16}px + env(safe-area-inset-top))`,
      left: `calc(${Math.round(HAMBURGER_CUTOUT_SIZE / 2) + 15}px + env(safe-area-inset-left))`,
    },
  },
  settingsLabel: {
    marginBottom: -26,
    marginLeft: -1,
  },
  layersContainer: {
    position: 'absolute',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    top: `calc(${HEADER_HEIGHT_MOBILE + MAP_CONTROL_GAP + LAYERS_LABEL_OFFSET.top / 2}px + env(safe-area-inset-top))`,
    right: MAP_CONTROL_RIGHT + LAYERS_LABEL_OFFSET.right,
    [theme.breakpoints.up(HEADER_BREAKPOINT_PX)]: {
      top: `calc(${HEADER_HEIGHT_DESKTOP + MAP_CONTROL_GAP + LAYERS_LABEL_OFFSET.top / 2}px + env(safe-area-inset-top))`,
    },
    [theme.breakpoints.up(SEARCH_DRAWER_BREAKPOINT_PX)]: {
      right:
        MAP_CONTROL_RIGHT + LAYERS_LABEL_OFFSET.right + SEARCH_DRAWER_WIDTH,
    },
  },
  layersLabel: {
    marginRight: theme.spacing(4),
    marginTop: -6,
  },
  legendsContainer: {
    position: 'absolute',
    display: 'flex',
    alignItems: 'flex-end',
    top: `calc(${HEADER_HEIGHT_MOBILE + MAP_CONTROL_GAP + MAP_CONTROL_SIZE + MAP_CONTROL_GAP + LEGEND_LABEL_OFFSET.top}px + env(safe-area-inset-top))`,
    right: MAP_CONTROL_RIGHT + LEGEND_LABEL_OFFSET.right + 5,
    [theme.breakpoints.up(HEADER_BREAKPOINT_PX)]: {
      top: `calc(${HEADER_HEIGHT_DESKTOP + MAP_CONTROL_GAP + MAP_CONTROL_SIZE + MAP_CONTROL_GAP + LEGEND_LABEL_OFFSET.top}px + env(safe-area-inset-top))`,
    },
    [theme.breakpoints.up(SEARCH_DRAWER_BREAKPOINT_PX)]: {
      right:
        MAP_CONTROL_RIGHT + LEGEND_LABEL_OFFSET.right + SEARCH_DRAWER_WIDTH + 5,
    },
  },
  legendsLabel: {
    marginBottom: -40,
    marginRight: -2,
  },
  cutoutTapTarget: {
    position: 'fixed',
    pointerEvents: 'auto',
  },
}));

/**
 * Measure the bounding rects of the three highlighted UI elements directly
 * from the DOM. This avoids hardcoded position math that can drift across
 * devices (e.g. stock Android edge-to-edge insets).
 */
const measureCutouts = (): Cutouts => {
  const cutouts: Cutouts = {};

  const hamburgerEl = document.querySelector<HTMLElement>(
    '[aria-controls="menu-appbar"]',
  );
  if (hamburgerEl) {
    const rect = hamburgerEl.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    cutouts.settings = {
      x: centerX - HAMBURGER_CUTOUT_SIZE / 2,
      y: centerY - HAMBURGER_CUTOUT_SIZE / 2,
      w: HAMBURGER_CUTOUT_SIZE,
      h: HAMBURGER_CUTOUT_SIZE,
    };
  }

  const [layersEl, legendEl] = document.querySelectorAll<HTMLElement>(
    '.mapboxgl-ctrl-top-right .MuiButtonBase-root',
  );
  if (layersEl) {
    const r = layersEl.getBoundingClientRect();
    cutouts.layers = { x: r.left, y: r.top, w: r.width, h: r.height };
  }
  if (legendEl) {
    const r = legendEl.getBoundingClientRect();
    cutouts.legend = { x: r.left, y: r.top, w: r.width, h: r.height };
  }

  return cutouts;
};

export const OnboardingWalkMe = (): JSX.Element | null => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();
  const { showTutorialSteps, setShowTutorialSteps } = useOnboardingState();
  const { toggleOpen } = useDrawerNavState();
  const { openLayersDrawer } = useLayersDrawerState();
  const maskId = useId();

  const [cutouts, setCutouts] = useState<Cutouts>({});

  useEffect(() => {
    if (!showTutorialSteps) return undefined;

    let frame = 0;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let attempts = 0;

    const measure = (): void => {
      cancelAnimationFrame(frame);
      clearTimeout(retryTimer);
      frame = requestAnimationFrame(() => {
        const measured = measureCutouts();
        setCutouts(measured);

        // On slow devices the map controls may not be in the DOM on first
        // paint. Retry until all three cutouts resolve or we hit the cap;
        // past that we render whatever was found and let the overlay continue
        // without highlighting any missing controls.
        const incomplete =
          !measured.settings || !measured.layers || !measured.legend;
        if (incomplete && attempts < MEASURE_MAX_RETRIES) {
          attempts += 1;
          retryTimer = setTimeout(measure, MEASURE_RETRY_INTERVAL_MS);
        }
      });
    };

    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(retryTimer);
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
    };
  }, [showTutorialSteps]);

  const handleSettingsClick = useCallback((): void => {
    setShowTutorialSteps(false);
    toggleOpen();
  }, [setShowTutorialSteps, toggleOpen]);

  const handleLayersClick = useCallback((): void => {
    setShowTutorialSteps(false);
    openLayersDrawer('layers');
  }, [setShowTutorialSteps, openLayersDrawer]);

  const handleLegendsClick = useCallback((): void => {
    setShowTutorialSteps(false);
    openLayersDrawer('legend');
  }, [setShowTutorialSteps, openLayersDrawer]);

  const handleDismiss = useCallback((): void => {
    setShowTutorialSteps(false);
  }, [setShowTutorialSteps]);

  if (!showTutorialSteps) {
    return null;
  }

  const cutoutStyle = (c: CutoutRect): CSSProperties => ({
    top: c.y,
    left: c.x,
    width: c.w,
    height: c.h,
  });

  return (
    <Box className={classes.root} data-testid="walk-me-backdrop">
      <svg className={classes.dim} aria-hidden="true">
        <defs>
          <mask id={maskId} maskUnits="userSpaceOnUse">
            <rect width="100%" height="100%" fill="white" />
            {(['settings', 'layers', 'legend'] as const).map((key) => {
              const c = cutouts[key];
              if (!c) return null;
              return (
                <rect
                  key={key}
                  x={c.x}
                  y={c.y}
                  width={c.w}
                  height={c.h}
                  rx={CUTOUT_RADIUS}
                  fill="black"
                />
              );
            })}
          </mask>
        </defs>
        <rect
          width="100%"
          height="100%"
          fill="rgba(0, 0, 0, 0.5)"
          mask={`url(#${maskId})`}
        />
      </svg>

      <div className={classes.container}>
        <Box data-testid="settings-step" className={classes.settingsContainer}>
          <VectorSettings />
          <Typography
            variant="body1"
            className={cx(classes.label, classes.settingsLabel)}
            fontWeight="fontWeightBold"
          >
            <Trans i18nKey="onboardingTooltip.settings">
              Settings &
              <br />
              Notifications
            </Trans>
          </Typography>
        </Box>

        <Box data-testid="layers-step" className={classes.layersContainer}>
          <VectorLayers />
          <Typography
            variant="body1"
            className={cx(classes.label, classes.layersLabel)}
          >
            <Trans i18nKey="onboardingTooltip.layers">
              <strong>Map Layers:</strong>
              <br />
              flight tracker,
              <br />
              weather & more
            </Trans>
          </Typography>
        </Box>

        <Box data-testid="legends-step" className={classes.legendsContainer}>
          <Typography
            variant="body1"
            className={cx(classes.label, classes.legendsLabel)}
            fontWeight="fontWeightBold"
          >
            <Trans i18nKey="onboardingTooltip.legend">
              Learn what map
              <br />
              symbols mean
            </Trans>
          </Typography>
          <VectorLegends />
        </Box>

        {cutouts.settings && (
          /* eslint-disable-next-line jsx-a11y/click-events-have-key-events */
          <div
            className={classes.cutoutTapTarget}
            style={cutoutStyle(cutouts.settings)}
            role="button"
            tabIndex={0}
            aria-label={t('onboardingTooltip.settings')}
            onClick={handleSettingsClick}
          />
        )}
        {cutouts.layers && (
          /* eslint-disable-next-line jsx-a11y/click-events-have-key-events */
          <div
            className={classes.cutoutTapTarget}
            style={cutoutStyle(cutouts.layers)}
            role="button"
            tabIndex={0}
            aria-label={t('onboardingTooltip.layers')}
            onClick={handleLayersClick}
          />
        )}
        {cutouts.legend && (
          /* eslint-disable-next-line jsx-a11y/click-events-have-key-events */
          <div
            className={classes.cutoutTapTarget}
            style={cutoutStyle(cutouts.legend)}
            role="button"
            tabIndex={0}
            aria-label={t('onboardingTooltip.legend')}
            onClick={handleLegendsClick}
          />
        )}

        <Button
          fullWidth
          size="large"
          onClick={handleDismiss}
          className={classes.button}
        >
          {t('onboardingTooltip.button')}
        </Button>
      </div>
    </Box>
  );
};
